import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  TagIcon, 
  EyeIcon,
  PlayIcon,
  LinkIcon,
  DocumentTextIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

interface ResearchItem {
  _id: string;
  title: string;
  description: string;
  year: number;
  category: string;
  journal: string;
  impact: string;
  content: {
    type: 'text' | 'image' | 'video' | 'link' | 'mixed';
    text?: string;
    images?: Array<{
      url: string;
      caption: string;
      alt: string;
    }>;
    video?: {
      url: string;
      title: string;
      description: string;
    };
    link?: {
      url: string;
      title: string;
      description: string;
    };
    mixed?: {
      text: string;
      images: Array<{
        url: string;
        caption: string;
        alt: string;
      }>;
      videos: Array<{
        url: string;
        title: string;
        description: string;
      }>;
      links: Array<{
        url: string;
        title: string;
        description: string;
      }>;
    };
  };
  authors: string[];
  tags: string[];
  viewCount: number;
  externalLinks?: Array<{
    title: string;
    url: string;
    description: string;
  }>;
  attachments?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
  }>;
  createdAt: string;
}

interface ResearchData {
  [year: string]: ResearchItem[];
}

const ResearchAchievements: React.FC = () => {
  const navigate = useNavigate();
  const [researchData, setResearchData] = useState<ResearchData>({});
  const [years, setYears] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const fetchResearchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedYear) params.append('year', selectedYear);
      if (selectedCategory) params.append('category', selectedCategory);
      
      const response = await fetch(`/api/research?${params}`);
      const result = await response.json();
      
      if (result.success) {
        setResearchData(result.data);
        setYears(result.years || []);
      }
    } catch (error) {
      console.error('获取研究成果失败:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedCategory]);

  useEffect(() => {
    fetchResearchData();
  }, [fetchResearchData]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/research/categories');
      const result = await response.json();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('获取分类失败:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderContent = (item: ResearchItem) => {
    const { content } = item;
    
    switch (content.type) {
      case 'text':
        return (
          <div 
            className="prose max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: content.text || '' }}
          />
        );
      
      case 'image':
        return (
          <div className="space-y-4">
            {content.images?.map((image, index) => (
              <div key={index} className="text-center">
                <img 
                  src={image.url} 
                  alt={image.alt || image.caption}
                  className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                />
                {image.caption && (
                  <p className="text-sm text-gray-600 mt-2">{image.caption}</p>
                )}
              </div>
            ))}
          </div>
        );
      
      case 'video':
        return (
          <div className="space-y-4">
            <div className="aspect-video">
              <iframe
                src={content.video?.url}
                title={content.video?.title}
                className="w-full h-full rounded-lg"
                frameBorder="0"
                allowFullScreen
              />
            </div>
            {content.video?.description && (
              <p className="text-gray-700">{content.video.description}</p>
            )}
          </div>
        );
      
      case 'link':
        return (
          <div className="space-y-4">
            <a 
              href={content.link?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              {content.link?.title}
            </a>
            {content.link?.description && (
              <p className="text-gray-700">{content.link.description}</p>
            )}
          </div>
        );
      
      case 'mixed':
        return (
          <div className="space-y-6">
            {content.mixed?.text && (
              <div 
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: content.mixed.text }}
              />
            )}
            
            {content.mixed?.images && content.mixed.images.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center">
                  <PhotoIcon className="w-5 h-5 mr-2" />
                  相关图片
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {content.mixed.images.map((image, index) => (
                    <div key={index} className="text-center">
                      <img 
                        src={image.url} 
                        alt={image.alt || image.caption}
                        className="max-w-full h-auto rounded-lg shadow-md"
                      />
                      {image.caption && (
                        <p className="text-sm text-gray-600 mt-2">{image.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {content.mixed?.videos && content.mixed.videos.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center">
                  <PlayIcon className="w-5 h-5 mr-2" />
                  相关视频
                </h4>
                <div className="space-y-4">
                  {content.mixed.videos.map((video, index) => (
                    <div key={index}>
                      <div className="aspect-video">
                        <iframe
                          src={video.url}
                          title={video.title}
                          className="w-full h-full rounded-lg"
                          frameBorder="0"
                          allowFullScreen
                        />
                      </div>
                      {video.description && (
                        <p className="text-gray-700 mt-2">{video.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {content.mixed?.links && content.mixed.links.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center">
                  <LinkIcon className="w-5 h-5 mr-2" />
                  相关链接
                </h4>
                <div className="space-y-2">
                  {content.mixed.links.map((link, index) => (
                    <a 
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-blue-600 hover:text-blue-800"
                    >
                      {link.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };


  // 列表式展示组件
  const renderResearchList = (item: ResearchItem) => (
    <div key={item._id} className="border-b border-gray-200 py-6 hover:bg-gray-50 transition-colors">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600 cursor-pointer" 
                onClick={() => navigate(`/research-achievements/${item._id}`)}>
              {item.title}
            </h3>
            <span className="text-sm text-gray-500">{item.year}</span>
          </div>
          
          <p className="text-gray-600 mb-3 line-clamp-2">{item.description}</p>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <TagIcon className="w-4 h-4" />
              <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded">
                {item.category}
              </span>
            </div>
            
            {item.authors && item.authors.length > 0 && (
              <div className="flex items-center gap-1">
                <span>作者：</span>
                <span>{item.authors.join(', ')}</span>
              </div>
            )}
            
            {item.journal && (
              <div className="flex items-center gap-1">
                <span className="text-green-600 bg-green-100 px-2 py-1 rounded text-xs">
                  📖 {item.journal}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-1">
              <EyeIcon className="w-4 h-4" />
              <span>{item.viewCount} 次浏览</span>
            </div>
          </div>
          
          {item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.tags.map((tag, index) => (
                <span key={index} className="text-xs bg-gray-200 px-2 py-1 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0">
          <button
            onClick={() => navigate(`/research-achievements/${item._id}`)}
            className="btn btn-primary btn-sm"
          >
            查看详情
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6">研究成果</h1>
      
      {/* 筛选器和视图切换 */}
      <div className="mb-8 p-4 bg-gray-50 rounded-lg">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">年份：</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="select select-sm"
              >
                <option value="">全部年份</option>
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">分类：</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="select select-sm"
              >
                <option value="">全部分类</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
          
        </div>
      </div>
      
      {/* 研究成果列表 */}
      {loading ? (
        <div className="text-center py-8">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(researchData).length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">暂无研究成果</p>
            </div>
          ) : (
            Object.entries(researchData)
              .sort(([a], [b]) => parseInt(b) - parseInt(a))
              .map(([year, items]) => (
                <section key={year}>
                  <h2 className="text-2xl font-semibold mb-6 flex items-center">
                    <CalendarIcon className="w-6 h-6 mr-2" />
                    {year}年研究成果
                  </h2>
                  <div className="space-y-0">
                    {items.map(renderResearchList)}
                  </div>
                </section>
              ))
          )}
        </div>
      )}
    </div>
  );
};

export default ResearchAchievements; 